"use client";

import { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
} from "recharts";
import {
  generateCurveData,
  generateMdcCurveData,
  ciHalfWidth,
  mdc,
  FREQUENCIES,
} from "../lib/statistics";

const FREQ_COLORS: Record<string, string> = {
  daily: "#10b981",
  three: "#f59e0b",
  weekly: "#ef4444",
};

interface ConfidenceChartProps {
  prompts: number;
  p: number;
  conf: number;
  mode: "ci" | "mdc";
}

export function ConfidenceChart({
  prompts,
  p,
  conf,
  mode,
}: ConfidenceChartProps) {
  const curveData = useMemo(
    () => (mode === "ci" ? generateCurveData(p, conf) : generateMdcCurveData(p, conf)),
    [p, conf, mode],
  );

  const markers = useMemo(
    () =>
      FREQUENCIES.map((f) => {
        const totalObs = prompts * f.runs;
        const val =
          mode === "ci"
            ? ciHalfWidth(totalObs, p, conf) * 100
            : mdc(totalObs, p, conf) * 100;
        return {
          key: f.key,
          label: f.label,
          observations: totalObs,
          value: Math.min(val, mode === "ci" ? 50 : 60),
        };
      }),
    [prompts, p, conf, mode],
  );

  const yMax = mode === "ci" ? 50 : 60;
  const yLabel = mode === "ci" ? "Margin of Error (±%)" : "Min. Detectable Change (%)";
  const title =
    mode === "ci"
      ? "Margin of Error vs. Total Monthly Observations"
      : "Minimum Detectable Change";
  const description =
    mode === "ci"
      ? "How precisely can you state your brand's mention rate? The curve shows worst-case error. Vertical markers show where each frequency lands. Lower is better."
      : "What's the smallest real shift in brand mention rate you can reliably detect? This matters for competitive comparisons.";

  return (
    <div className="rounded-2xl border bg-card p-6">
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>

      <div className="mt-4 flex flex-wrap gap-4">
        {FREQUENCIES.map((f) => (
          <div key={f.key} className="flex items-center gap-2 text-xs text-muted-foreground">
            <div
              className="h-0.5 w-5 rounded"
              style={{ backgroundColor: FREQ_COLORS[f.key] }}
            />
            {f.label} ({f.runs} runs/prompt/mo)
          </div>
        ))}
      </div>

      <div className="mt-4 h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={curveData}
            margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
          >
            <defs>
              <linearGradient id={`curveGrad-${mode}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
            <XAxis
              dataKey="observations"
              type="number"
              domain={[5, 600]}
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              label={{
                value: "Total Monthly Observations (prompts × runs)",
                position: "insideBottom",
                offset: -2,
                style: { fontSize: 10, fill: "var(--muted-foreground)" },
              }}
            />
            <YAxis
              domain={[0, yMax]}
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => `${v}%`}
              width={45}
              label={{
                value: yLabel,
                angle: -90,
                position: "insideLeft",
                offset: 10,
                style: { fontSize: 10, fill: "var(--muted-foreground)" },
              }}
            />
            <RechartsTooltip
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0].payload as { observations: number; value: number };
                return (
                  <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
                    <div className="font-semibold">{d.observations} observations</div>
                    <div className="mt-1 text-muted-foreground">
                      {mode === "ci" ? "Margin of error" : "Min. detectable change"}:{" "}
                      <span className="font-medium text-foreground">
                        ±{d.value.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#10b981"
              strokeWidth={2}
              fill={`url(#curveGrad-${mode})`}
            />
            {markers.map((m) => (
              <ReferenceLine
                key={m.key}
                x={m.observations}
                stroke={FREQ_COLORS[m.key]}
                strokeWidth={1.5}
                strokeDasharray="5 3"
                label={{
                  value: `${m.label} ±${m.value.toFixed(1)}%`,
                  position: "insideTopRight",
                  style: {
                    fontSize: 10,
                    fill: FREQ_COLORS[m.key],
                    fontWeight: 500,
                  },
                }}
              />
            ))}
            {markers.map((m) => (
              <ReferenceDot
                key={`dot-${m.key}`}
                x={m.observations}
                y={m.value}
                r={5}
                fill={FREQ_COLORS[m.key]}
                stroke="var(--background)"
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
